import { useCallback, useEffect, useState } from 'react'
import { useDpopFetch } from './useDpopFetch'

// useEarnings — fetches the aggregated 24h / 7d / 30d totals for a shop
// from GET /v1/shops/:handle/earnings-summary. Same auth as useOrders
// (v2 JWT via resolveShopAuth) and same "preserve prior data on refresh"
// pattern so the header numbers don't flash blank.

const API_URL = (import.meta.env.VITE_HEADLO_API_URL as string | undefined) ?? 'https://api.headlo.com'

export interface EarningsBucket {
  count: number
  cents: number
}

export interface EarningsSummary {
  handle:      string
  brand_name:  string
  has_shopify: boolean
  currency:    string             // 'USD' for now; hardcoded on the server
  buckets: {
    today: EarningsBucket
    week:  EarningsBucket
    month: EarningsBucket
  }
}

export type EarningsStatus =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready';   summary: EarningsSummary; refreshing: boolean }
  | { status: 'error';   message: string }

export interface UseEarningsResult {
  state:   EarningsStatus
  refresh: () => void
}

export function useEarnings(handle: string | null): UseEarningsResult {
  const authFetch = useDpopFetch()
  const [state, setState] = useState<EarningsStatus>({ status: 'idle' })
  const [tick,  setTick]  = useState(0)

  const refresh = useCallback(() => setTick(t => t + 1), [])

  useEffect(() => {
    if (!handle) {
      setState({ status: 'idle' })
      return
    }

    let alive = true

    setState(prev => (prev.status === 'ready'
      ? { status: 'ready', summary: prev.summary, refreshing: true }
      : { status: 'loading' }))

    void (async () => {
      try {
        const url = `${API_URL.replace(/\/$/, '')}/v1/shops/${encodeURIComponent(handle)}/earnings-summary`
        const res = await authFetch(url)
        if (!alive) return
        if (!res.ok) {
          setState({ status: 'error', message: `${res.status} ${res.statusText}` })
          return
        }
        const body = await res.json() as EarningsSummary
        setState({ status: 'ready', summary: body, refreshing: false })
      } catch (e) {
        if (!alive) return
        setState({ status: 'error', message: (e as Error).message ?? String(e) })
      }
    })()

    return () => { alive = false }
  }, [handle, tick, authFetch])

  return { state, refresh }
}

// ── Formatters ────────────────────────────────────────────────────────────

/**
 * Format cents as a currency string. Uses Intl.NumberFormat with the
 * shop's currency code (USD only for now — server hardcodes it, and
 * currency-per-shop is a TODO on the server side).
 */
export function formatMoney(cents: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style:                 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(cents / 100)
  } catch {
    // Fallback for unknown currency codes — plain "$X.XX"
    return `$${(cents / 100).toFixed(2)}`
  }
}
